import { controlPointSchema, type ControlPoint, type SegmentType } from '@rcs/data-schema';
import type { Vec3 } from '@rcs/domain';
import type { Command } from './command';
import type { EditorState } from './state';
import { findControlPoint } from './state';

/** 制御点のプロパティの変更。指定した項目だけを変える。 */
export interface ControlPointPatch {
  readonly position?: Vec3;
  /** バンク角 [rad] */
  readonly bank?: number;
  /** 区間種別。変えると、パラメータは新しい種別の既定値になる（`segmentParams` を併せて指定しない限り） */
  readonly segment?: SegmentType;
  /** 区間種別のパラメータ（部分的でよい。省略した項目は、既定値または現在の値） */
  readonly segmentParams?: Readonly<Record<string, number>>;
}

/** 変更の検証結果。 */
export type PatchResult =
  | { readonly ok: true; readonly point: ControlPoint }
  | { readonly ok: false; readonly message: string };

/** 項目名の表示（検証の失敗を、どの項目か分かるように伝える）。 */
const FIELD_NAMES: Readonly<Record<string, string>> = {
  position: '位置',
  bank: 'バンク角',
  liftSpeed: '巻き上げ速度',
  maxDeceleration: '最大減速度',
  targetSpeed: '目標速度',
  dispatchSpeed: '送り出し速度',
  maxAcceleration: '最大加速度',
  segment: '区間種別',
};

/**
 * 発進加速（`launch`）に切り替えたときの既定のパラメータ。保存形式では必須の項目のため、
 * エディターで補う（目標速度 25 m/s = 90 km/h、最大加速度 10 m/s² ≈ 1 G）。
 */
export const DEFAULT_LAUNCH_PARAMS = { targetSpeed: 25, maxAcceleration: 10 } as const;

/**
 * 制御点に変更を適用し、スキーマ（範囲・型）を満たすか検証する。元の制御点は変えない。
 * 満たさなければ、どの項目が不正かを示す日本語の理由を返す。
 */
export function patchControlPoint(point: ControlPoint, patch: ControlPointPatch): PatchResult {
  const segment = patch.segment ?? point.segment;
  const currentParams =
    'segmentParams' in point && point.segment === segment
      ? (point.segmentParams as Record<string, number>)
      : {};
  const defaults: Record<string, number> =
    segment === 'launch' && point.segment !== 'launch' ? { ...DEFAULT_LAUNCH_PARAMS } : {};
  const params = { ...defaults, ...currentParams, ...patch.segmentParams };
  const hasParams = Object.keys(params).length > 0;
  const candidate = {
    id: point.id,
    position: patch.position ?? point.position,
    bank: patch.bank ?? point.bank,
    segment,
    ...(hasParams ? { segmentParams: params } : {}),
  };
  const result = controlPointSchema.safeParse(candidate);
  if (result.success) return { ok: true, point: result.data };
  const issue = result.error.issues[0];
  const key = issue?.path.find((part) => typeof part === 'string' && part in FIELD_NAMES);
  const field = typeof key === 'string' ? (FIELD_NAMES[key] ?? '値') : '値';
  return { ok: false, message: `${field}が範囲外、または数値ではありません。` };
}

/** 検証済みの制御点で、コースの制御点を置き換えた状態。 */
function withPoint(state: EditorState, point: ControlPoint): EditorState {
  return {
    ...state,
    course: {
      ...state.course,
      controlPoints: state.course.controlPoints.map((p) => (p.id === point.id ? point : p)),
    },
  };
}

/**
 * 制御点のプロパティを変える操作（Undo / Redo の単位）。
 * 存在しない ID・検証に失敗する変更・何も変わらない変更は `undefined`。
 */
export function updateControlPointCommand(
  state: EditorState,
  id: string,
  patch: ControlPointPatch,
): Command<EditorState> | undefined {
  const before = findControlPoint(state, id);
  if (!before) return undefined;
  const result = patchControlPoint(before, patch);
  if (!result.ok) return undefined;
  const after = result.point;
  if (JSON.stringify(after) === JSON.stringify(before)) return undefined;
  return {
    label: '制御点を変更',
    apply: (current) => withPoint(current, after),
    revert: (current) => withPoint(current, before),
  };
}

/** コースを閉ループ（周回）にできるか。周回コースには制御点が 3 点以上必要。 */
export function canSetClosed(state: EditorState, closed: boolean): boolean {
  return !closed || state.course.controlPoints.length >= 3;
}

/**
 * コースの周回・開いたコースを切り替える操作（Undo / Redo の単位）。
 * 変わらない、または周回にできない（制御点が 2 点）ときは `undefined`。
 */
export function setClosedCommand(
  state: EditorState,
  closed: boolean,
): Command<EditorState> | undefined {
  if (state.course.closed === closed || !canSetClosed(state, closed)) return undefined;
  const previous = state.course.closed;
  return {
    label: closed ? 'コースを周回にする' : 'コースを開く',
    apply: (current) => ({ ...current, course: { ...current.course, closed } }),
    revert: (current) => ({ ...current, course: { ...current.course, closed: previous } }),
  };
}
